import React, { useEffect, useMemo, useState } from 'react';
import { rolesApi } from '../../services/api';
import { invalidateLookup } from '../../services/lookupCache';
import toast from 'react-hot-toast';
import { HiPlus, HiPencil, HiTrash, HiX, HiLockClosed } from 'react-icons/hi';
import ConfirmDialog from '../../components/ConfirmDialog';

const ACTIONS = [
  { key: 'read', label: 'Read' },
  { key: 'create', label: 'Create' },
  { key: 'update', label: 'Update' },
  { key: 'delete', label: 'Delete' },
];

const formatRoleLabel = (name) => {
  if (!name) return '';
  return String(name)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const emptyMatrix = (modules) => {
  const matrix = {};
  modules.forEach((m) => {
    matrix[m.key] = { read: false, create: false, update: false, delete: false };
  });
  return matrix;
};

const permissionsToMatrix = (modules, permissions) => {
  const matrix = emptyMatrix(modules);
  modules.forEach((m) => {
    const src = permissions?.[m.key];
    if (!src || typeof src !== 'object') return;
    ACTIONS.forEach(({ key }) => {
      matrix[m.key][key] = src[key] === true;
    });
  });
  return matrix;
};

const matrixToPermissions = (matrix) => {
  const out = {};
  Object.entries(matrix || {}).forEach(([module, actions]) => {
    const entry = {};
    ACTIONS.forEach(({ key }) => {
      if (actions[key] === true) entry[key] = true;
    });
    if (Object.keys(entry).length) out[module] = entry;
  });
  return out;
};

const Roles = () => {
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formMatrix, setFormMatrix] = useState({});
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, onConfirm: null });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rolesRes, modulesRes] = await Promise.all([
        rolesApi.getAll(),
        rolesApi.getModules(),
      ]);
      setRoles(rolesRes.data.data || []);
      setModules(modulesRes.data.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAdd = () => {
    setEditingRole(null);
    setFormName('');
    setFormDescription('');
    const matrix = emptyMatrix(modules);
    if (matrix.dashboard) matrix.dashboard.read = true;
    setFormMatrix(matrix);
    setShowForm(true);
  };

  const handleEdit = (role) => {
    if (role.is_builtin) return;
    setEditingRole(role);
    setFormName(role.name || '');
    setFormDescription(role.description || '');
    setFormMatrix(permissionsToMatrix(modules, role.permissions));
    setShowForm(true);
  };

  const toggleAction = (moduleKey, actionKey) => {
    setFormMatrix((prev) => {
      const current = prev[moduleKey] || { read: false, create: false, update: false, delete: false };
      const nextVal = !current[actionKey];
      const next = { ...current, [actionKey]: nextVal };
      if (actionKey !== 'read' && nextVal) next.read = true;
      if (actionKey === 'read' && !nextVal) {
        next.create = false;
        next.update = false;
        next.delete = false;
      }
      return { ...prev, [moduleKey]: next };
    });
  };

  const toggleModuleAll = (moduleKey) => {
    setFormMatrix((prev) => {
      const current = prev[moduleKey] || { read: false, create: false, update: false, delete: false };
      const allOn = ACTIONS.every(({ key }) => current[key] === true);
      const nextVal = !allOn;
      return {
        ...prev,
        [moduleKey]: {
          read: nextVal,
          create: nextVal,
          update: nextVal,
          delete: nextVal,
        },
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formName.trim()) {
      toast.error('Role name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: formName,
        description: formDescription,
        permissions: matrixToPermissions(formMatrix),
      };
      if (editingRole) {
        await rolesApi.update(editingRole.id, payload);
        toast.success('Role updated');
      } else {
        await rolesApi.create(payload);
        toast.success('Role created');
      }
      invalidateLookup('roles:list');
      setShowForm(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (role) => {
    if (role.is_builtin) return;
    setConfirmDialog({
      isOpen: true,
      onConfirm: async () => {
        try {
          await rolesApi.delete(role.id);
          invalidateLookup('roles:list');
          toast.success('Role deleted');
          fetchData();
        } catch (error) {
          toast.error(error.response?.data?.message || 'Failed to delete role');
        }
      },
    });
  };

  const builtinRoles = useMemo(() => roles.filter((r) => r.is_builtin), [roles]);
  const customRoles = useMemo(() => roles.filter((r) => !r.is_builtin), [roles]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-gray-900">Roles</h1>
          <p className="text-gray-500 mt-1">
            Create custom roles and set read / create / update / delete per module
          </p>
        </div>
        <button
          type="button"
          onClick={handleAdd}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <HiPlus className="w-5 h-5" />
          Add Role
        </button>
      </div>

      <RoleTable
        title="Built-in roles"
        empty="No built-in roles found"
        roles={builtinRoles}
        locked
      />
      <RoleTable
        title="Custom roles"
        empty="No custom roles yet. Add a role to grant staff a specific set of modules."
        roles={customRoles}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingRole ? `Edit ${formatRoleLabel(editingRole.name)}` : 'New role'}
              </h2>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg"
              >
                <HiX className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
              <div className="px-5 py-4 space-y-4 overflow-y-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input
                      type="text"
                      value={formName}
                      onChange={(e) => setFormName(e.target.value)}
                      placeholder="e.g. Doctor"
                      className="input w-full"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <input
                      type="text"
                      value={formDescription}
                      onChange={(e) => setFormDescription(e.target.value)}
                      placeholder="Optional"
                      className="input w-full"
                    />
                  </div>
                </div>

                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium text-gray-600">Module</th>
                        {ACTIONS.map((a) => (
                          <th key={a.key} className="px-3 py-2 font-medium text-gray-600 text-center w-24">
                            {a.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {modules.map((mod) => {
                        const row = formMatrix[mod.key] || {};
                        const allOn = ACTIONS.every(({ key }) => row[key] === true);
                        return (
                          <tr key={mod.key} className="border-t border-gray-100">
                            <td className="px-3 py-2">
                              <label className="inline-flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={allOn}
                                  onChange={() => toggleModuleAll(mod.key)}
                                  className="rounded border-gray-300 text-primary-600"
                                />
                                <span className="font-medium text-gray-800">{mod.label}</span>
                              </label>
                            </td>
                            {ACTIONS.map((a) => (
                              <td key={a.key} className="px-3 py-2 text-center">
                                <input
                                  type="checkbox"
                                  checked={row[a.key] === true}
                                  onChange={() => toggleAction(mod.key, a.key)}
                                  className="rounded border-gray-300 text-primary-600"
                                  aria-label={`${mod.label} ${a.label}`}
                                />
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-500">
                  Dashboard read is always included. Custom roles see all clinic patients (not only assigned ones).
                </p>
              </div>
              <div className="px-5 py-4 border-t border-gray-100 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {saving ? 'Saving…' : editingRole ? 'Save changes' : 'Create role'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ isOpen: false, onConfirm: null })}
        onConfirm={confirmDialog.onConfirm}
        title="Delete role"
        message="This role will be permanently deleted. Staff must be reassigned first."
        confirmText="Delete"
      />
    </div>
  );
};

const RoleTable = ({ title, empty, roles, locked, onEdit, onDelete }) => (
  <div className="card">
    <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
    {roles.length === 0 ? (
      <p className="text-sm text-gray-500">{empty}</p>
    ) : (
      <div className="divide-y divide-gray-100">
        {roles.map((role) => (
          <div key={role.id} className="flex items-start justify-between gap-4 py-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h4 className="font-semibold text-gray-900">{formatRoleLabel(role.name)}</h4>
                {locked || role.is_builtin ? (
                  <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                    <HiLockClosed className="w-3 h-3" />
                    Locked
                  </span>
                ) : null}
              </div>
              {role.description ? (
                <p className="text-sm text-gray-500 mt-0.5">{role.description}</p>
              ) : (
                <p className="text-sm text-gray-400 mt-0.5">{role.name}</p>
              )}
            </div>
            {!locked && !role.is_builtin ? (
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={() => onEdit(role)}
                  className="p-1.5 text-primary-600 hover:bg-primary-50 rounded-lg"
                  title="Edit"
                >
                  <HiPencil className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => onDelete(role)}
                  className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                  title="Delete"
                >
                  <HiTrash className="w-4 h-4" />
                </button>
              </div>
            ) : null}
          </div>
        ))}
      </div>
    )}
  </div>
);

export default Roles;
